import { createSlice } from '@reduxjs/toolkit';
import { DEFAULT_CHATBOT_ERROR_MESSAGE, REQUEST_STATUS } from './constants';
import { SENDER_TYPES } from '../utils/senderTypes';
/* eslint-disable no-param-reassign */
// ^ we are using immer under the hood which allows us to "mutate" state so this is safe to disable
// https://redux-toolkit.js.org/usage/immer-reducers

// Kept for backward compatibility with existing imports/tests that source
// REQUEST_STATUS from chatbotSlice directly.
export { REQUEST_STATUS };

/**
 * @typedef {Object} ChatbotState
 * @property {boolean} hasAcceptedDisclaimer
 * @property {{ id: string, sender: 'user' | 'bot', text: string, options?: string[], optionType?: 'icon' | 'text', isAiGenerated?: boolean, isFeedback?: boolean, isSignInPrompt?: boolean, feedbackTargetMessageId?: string, feedbackAcknowledgementTargetMessageId?: string, feedbackSubmitted?: boolean, feedbackPending?: boolean }[]} messages
 * @property {'idle'|'loading'|'succeeded'|'failed'} requestStatus
 * @property {string | null} error
 */

/** @type {ChatbotState} */
const initialState = {
  hasAcceptedDisclaimer: false,
  messages: [],
  requestStatus: REQUEST_STATUS.IDLE,
  error: null,
};

const createMessageId = prefix => `${prefix}-${Date.now()}-${Math.random()}`;

const chatbotSlice = createSlice({
  name: 'chatbot',
  initialState,
  reducers: {
    acceptDisclaimer: state => {
      state.hasAcceptedDisclaimer = true;
    },
    addUserMessage: (state, action) => {
      state.messages.push({
        id: createMessageId('user'),
        sender: SENDER_TYPES.USER,
        text: action.payload,
      });
    },
    addRestoredUserMessage: (state, action) => {
      state.messages.push({
        id: action.payload.id,
        sender: SENDER_TYPES.USER,
        text: action.payload.text,
      });
    },
    resetConversation: state => {
      state.messages = [];
      state.requestStatus = REQUEST_STATUS.IDLE;
      state.error = null;
    },
    addAssistantMessage: (state, action) => {
      if (!action.payload) {
        return;
      }

      const payload =
        typeof action.payload === 'string'
          ? {
              options: [],
              text: action.payload,
            }
          : action.payload;

      if (!payload?.text) {
        return;
      }

      const message = {
        id: payload.id || createMessageId(SENDER_TYPES.BOT),
        options: payload.options || [],
        sender: SENDER_TYPES.BOT,
        text: payload.text,
      };

      if (payload.optionType) {
        message.optionType = payload.optionType;
      }

      if (payload.isAiGenerated) {
        message.isAiGenerated = true;
      }

      if (payload.isFeedback) {
        message.isFeedback = true;
      }

      if (payload.isSignInPrompt) {
        message.isSignInPrompt = true;
      }

      if (payload.feedbackTargetMessageId) {
        message.feedbackTargetMessageId = payload.feedbackTargetMessageId;
      }

      if (payload.feedbackAcknowledgementTargetMessageId) {
        message.feedbackAcknowledgementTargetMessageId =
          payload.feedbackAcknowledgementTargetMessageId;
      }

      if (message.feedbackAcknowledgementTargetMessageId) {
        const matchingFeedbackPromptIndex = state.messages.findIndex(
          existingMessage =>
            existingMessage.sender === SENDER_TYPES.BOT &&
            existingMessage.isFeedback &&
            existingMessage.feedbackTargetMessageId ===
              message.feedbackAcknowledgementTargetMessageId,
        );

        if (matchingFeedbackPromptIndex >= 0) {
          state.messages[matchingFeedbackPromptIndex] = message;
          return;
        }
      }

      state.messages.push(message);
    },
    markFeedbackSubmitted: (state, action) => {
      const feedbackTargetMessageId = action.payload;

      if (!feedbackTargetMessageId) {
        return;
      }

      state.messages.forEach(message => {
        if (
          message.sender === SENDER_TYPES.BOT &&
          message.isFeedback &&
          message.feedbackTargetMessageId === feedbackTargetMessageId
        ) {
          message.feedbackSubmitted = true;
          message.feedbackPending = false;
        }
      });
    },
    setFeedbackPending: (state, action) => {
      const messageId = action.payload;

      if (!messageId) {
        return;
      }

      const target = state.messages.find(
        message =>
          message.id === messageId &&
          message.sender === SENDER_TYPES.BOT &&
          message.isFeedback,
      );

      if (target) {
        target.feedbackPending = true;
      }
    },
    clearFeedbackPending: (state, action) => {
      const messageId = action.payload;

      if (!messageId) {
        return;
      }

      const target = state.messages.find(
        message =>
          message.id === messageId &&
          message.sender === SENDER_TYPES.BOT &&
          message.isFeedback,
      );

      if (target) {
        target.feedbackPending = false;
      }
    },
    setRequestStatus: (state, action) => {
      state.requestStatus = action.payload;
    },
    setChatError: (state, action) => {
      state.error = action.payload || DEFAULT_CHATBOT_ERROR_MESSAGE;
    },
    clearChatError: state => {
      state.error = null;
    },
  },
});

const selectChatbotState = state => state.chatbot || initialState;

export const selectChatbotHasAcceptedDisclaimer = state =>
  selectChatbotState(state).hasAcceptedDisclaimer;

export const selectChatbotMessages = state =>
  selectChatbotState(state).messages;

export const selectChatbotRequestStatus = state =>
  selectChatbotState(state).requestStatus;

export const selectChatbotError = state => selectChatbotState(state).error;

export const chatbotActions = chatbotSlice.actions;

export default chatbotSlice.reducer;
